import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { orders as ordersApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Eye, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';

const AdminOrders = () => {
  usePageTitle('Manage Orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await ordersApi.getAll({ limit: 100 });
      setOrders(response.data.orders);
      setSelectedOrders([]);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await ordersApi.updateStatus(orderId, newStatus);
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      toast.success('Order status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Single order delete with confirmation dialog
  const openDeleteDialog = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    setDeleting(true);
    try {
      await ordersApi.delete(orderToDelete.id);
      setOrders(orders.filter(order => order.id !== orderToDelete.id));
      setSelectedOrders(selectedOrders.filter(id => id !== orderToDelete.id));
      toast.success(`Order ${orderToDelete.order_number} deleted successfully`);
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  // Bulk delete
  const confirmBulkDelete = async () => {
    setDeleting(true);
    try {
      const response = await ordersApi.bulkDelete(selectedOrders);
      const { deleted_count, failed_ids } = response.data;
      
      setOrders(orders.filter(order => !selectedOrders.includes(order.id)));
      setSelectedOrders([]);
      setBulkDeleteDialogOpen(false);
      
      if (deleted_count > 0) {
        toast.success(`${deleted_count} order(s) deleted successfully`);
      }
      if (failed_ids && failed_ids.length > 0) {
        toast.error(`Failed to delete ${failed_ids.length} order(s)`);
      }
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete orders. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Select/Deselect handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrders(filteredOrders.map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId, checked) => {
    if (checked) {
      setSelectedOrders([...selectedOrders, orderId]);
    } else {
      setSelectedOrders(selectedOrders.filter(id => id !== orderId));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      processing: 'bg-purple-100 text-purple-800 border-purple-200',
      shipped: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(order => order.status === filter);

  const allSelected = filteredOrders.length > 0 && selectedOrders.length === filteredOrders.length;
  const someSelected = selectedOrders.length > 0 && selectedOrders.length < filteredOrders.length;

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 bg-white" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="admin-orders">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Orders</h1>
          <p className="text-[#64748B]">{orders.length} total orders</p>
        </div>
        <div className="flex gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadOrders}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedOrders.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-4 flex items-center justify-between rounded-lg">
          <span className="text-blue-800 font-medium">
            {selectedOrders.length} order(s) selected
          </span>
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => setBulkDeleteDialogOpen(true)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Selected
          </Button>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white border border-[#E2E8F0] overflow-hidden">
        {filteredOrders.length === 0 ? (
          <div className="p-12 text-center text-[#64748B]">
            No orders found
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="p-4 w-12">
                  <Checkbox 
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    className={someSelected ? "data-[state=checked]:bg-blue-300" : ""}
                  />
                </th>
                <th className="text-left p-4 text-sm font-medium text-[#64748B]">Order</th>
                <th className="text-left p-4 text-sm font-medium text-[#64748B]">Customer</th>
                <th className="text-left p-4 text-sm font-medium text-[#64748B]">Items</th>
                <th className="text-left p-4 text-sm font-medium text-[#64748B]">Total</th>
                <th className="text-left p-4 text-sm font-medium text-[#64748B]">Status</th>
                <th className="text-left p-4 text-sm font-medium text-[#64748B]">Date</th>
                <th className="text-left p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {filteredOrders.map((order) => (
                <tr 
                  key={order.id} 
                  className={`hover:bg-[#F8FAFC] ${selectedOrders.includes(order.id) ? 'bg-blue-50' : ''}`}
                  data-testid={`order-row-${order.id}`}
                >
                  <td className="p-4">
                    <Checkbox 
                      checked={selectedOrders.includes(order.id)}
                      onCheckedChange={(checked) => handleSelectOrder(order.id, checked)}
                    />
                  </td>
                  <td className="p-4 font-mono text-sm">{order.order_number}</td>
                  <td className="p-4">
                    <p className="font-medium text-[#0F172A]">{order.company_name}</p>
                    <p className="text-xs text-[#64748B]">{order.user_email}</p>
                  </td>
                  <td className="p-4 text-sm">
                    {order.items.length} items ({order.items.reduce((acc, item) => acc + item.total_quantity, 0)} units)
                  </td>
                  <td className="p-4 font-semibold">${order.total.toFixed(2)}</td>
                  <td className="p-4">
                    <Select
                      value={order.status}
                      onValueChange={(value) => handleStatusUpdate(order.id, value)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <Badge className={`${getStatusColor(order.status)} border`}>
                          {order.status}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-sm text-[#64748B]">
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      <Link to={`/orders/${order.id}`}>
                        <Button variant="ghost" size="sm" title="View Order">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openDeleteDialog(order)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        title="Delete Order"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Single Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Delete Order
            </DialogTitle>
            <DialogDescription className="pt-4">
              Are you sure you want to delete order <span className="font-mono font-bold">{orderToDelete?.order_number}</span>?
              <br /><br />
              This will permanently remove the order and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteOrder} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Bulk Delete
            </DialogTitle>
            <DialogDescription className="pt-4">
              Are you sure you want to delete <span className="font-bold">{selectedOrders.length} order(s)</span>?
              <br /><br />
              This will permanently remove all selected orders and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : `Delete ${selectedOrders.length} Order(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
